import { type ApiError, apiErrorSchema } from '@tailoring-crm/shared';

/**
 * Three failure shapes, and only three, so every caller can branch exhaustively:
 *
 * - {@link ApiRequestError} — the server answered and said no. It carries the machine-readable
 *   `code` and, for a validation failure, the per-field messages that go back into the form.
 * - {@link NetworkError} — nothing answered. The request may or may not have been applied, so
 *   only idempotent or queued writes may be retried automatically.
 * - {@link OfflineError} — we never even tried, because the device is offline and the caller
 *   did not mark the write as queueable.
 */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown> | undefined;
  readonly fieldErrors: Record<string, string[]> | undefined;

  constructor(status: number, payload: ApiError) {
    super(payload.message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = payload.code;
    this.details = payload.details;
    this.fieldErrors = payload.fieldErrors;
  }

  /** True when the server rejected specific inputs and named them. */
  hasFieldErrors(): boolean {
    return this.fieldErrors !== undefined && Object.keys(this.fieldErrors).length > 0;
  }
}

/**
 * NOTE on the default messages below: they are diagnostic text for a developer reading a
 * stack trace, never copy shown to a shop. What the owner sees for a lost connection comes
 * from `offline.banner.*` in the locale file, chosen by the banner, not from `error.message`.
 */
export class NetworkError extends Error {
  constructor(message = 'The request could not reach the server', cause?: unknown) {
    super(message);
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

export class OfflineError extends Error {
  constructor(message = 'The device is offline') {
    super(message);
    this.name = 'OfflineError';
  }
}

export function isApiRequestError(error: unknown): error is ApiRequestError {
  return error instanceof ApiRequestError;
}

export function isNetworkError(error: unknown): error is NetworkError {
  return error instanceof NetworkError;
}

export function isOfflineError(error: unknown): error is OfflineError {
  return error instanceof OfflineError;
}

/**
 * Whether an automatic retry could plausibly succeed. A 4xx will fail identically forever;
 * a dropped connection, a 429 or a 5xx will not.
 */
export function isRetryableError(error: unknown): boolean {
  if (isNetworkError(error)) return true;
  if (isApiRequestError(error)) return error.status >= 500 || error.status === 429;
  return false;
}

/** A 409 from the server means someone else changed the record first. */
export function isConflictError(error: unknown): boolean {
  return isApiRequestError(error) && error.status === 409;
}

export function isUnauthenticatedError(error: unknown): boolean {
  return isApiRequestError(error) && error.status === 401;
}

/**
 * Coerce whatever the server sent into the shared error envelope. A proxy or a load balancer
 * can return HTML on a bad day, and the UI must still say something specific rather than
 * throwing a parse error on top of the original failure.
 */
export function toApiError(status: number, body: unknown, fallbackMessage: string): ApiError {
  const parsed = apiErrorSchema.safeParse(body);
  if (parsed.success) return parsed.data;
  return { code: `http_${status}`, message: fallbackMessage };
}
